/* eslint-disable react-dom/no-unsafe-iframe-sandbox */
/* eslint-disable no-alert */
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Heading, IconBin, Text } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import AudioPlayer from "react-h5-audio-player";
import { useTranslation } from "react-i18next";
import "react-h5-audio-player/lib/styles.css";

import type { RecordingData } from "@/api/queries";

import { useDeleteRecording } from "@/api/mutations";
import { useGetMyAccount, useGetMyRecordingsDetailed, useGetRecordingMetas } from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import LoadingSmall from "@/components/LoadingSmall";
import useAppStore from "@/store/appStateZustand";

import DeleteRecordingModal from "./DeleteRecordingModal";
import RecordingAttachmentForm from "./RecordingAttachmentForm";

type SongRecordingProps = {
  recording: RecordingData;
  last: boolean;
};

function SongRecording({ recording, last }: SongRecordingProps) {
  const recordingMetas = useGetRecordingMetas({ id: recording.workcode ?? "" });
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const adminRights = hasAdminRights(myAccount?.data);
  const { t } = useTranslation("songs");

  const queryClient = useQueryClient();
  const deleteRecordingMutation = useDeleteRecording();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState<RecordingData | null>(null);

  const recordingInfo = recordingMetas.isSuccess
    ? recordingMetas.data?.find(
        r => r.id === recording.recordingidentifier || r.id === recording.uicbarcode,
      )
    : undefined;

  const hasMp3 = recording.s3presignedurl != null && recording.s3presignedurl !== "";
  const attachMp3lbl = hasMp3 ? t("songs:attach-new-mp3") : t("songs:attach-mp3");

  const [recordingAttachmentModalIsOpen, setRecordingAttachmentModalIsOpen] = useState(false);
  const [mp3PlayerModalIsOpen, setMp3PlayerModalIsOpen] = useState(false);

  const aac = recording.artistaccountcode != null && recording.artistaccountcode !== ""
    ? recording.artistaccountcode
    : selectedAccount;

  const recordings = useGetMyRecordingsDetailed({
    aac,
    wc: recording.workcode ?? "",
  });

  const [recordingMp3Url, setRecordingMp3Url] = useState("");

  const handleConfirmDelete = (rec: RecordingData | null = recordingToDelete): void => {
    if (rec === null) {
      console.warn("No recording selected for deletion.");
      return;
    }
    setIsDeleting(true);

    deleteRecordingMutation.mutate(
      {
        artistaccountcode: rec.artistaccountcode,
        recordingidentifier: rec.recordingidentifier,
        recordingamendmentidentifier: rec.recordingamendmentidentifier,
        workcode: rec.workcode,
      },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
          setRecordingToDelete(null);
          void queryClient.invalidateQueries({ queryKey: ["recordings"] });
          void recordings.refetch();
          setIsDeleted(true);
        },
        onError: (error: Error) => {
          console.error("Failed to delete recording:", error);
          window.alert(`Failed to delete recording: ${error.message}`);
          setShowDeleteModal(false);
          setRecordingToDelete(null);
          setIsDeleting(false);
        },
      },
    );
  };

  const handleDeleteRecordingClick = (): void => {
    const showWarning = localStorage.getItem("showDeleteRecordingWarning");
    if (showWarning === "false") {
      handleConfirmDelete(recording);
    }
    else {
      setRecordingToDelete(recording);
      setShowDeleteModal(true);
    }
  };

  return (
    <>
      <DeleteRecordingModal
        isOpen={showDeleteModal}
        onConfirm={() => handleConfirmDelete()}
        onClose={() => setShowDeleteModal(false)}
      />

      <Dialog open={recordingAttachmentModalIsOpen} onOpenChange={setRecordingAttachmentModalIsOpen}>
        <DialogContent className="max-w-md">
          {recordingAttachmentModalIsOpen && (
            <RecordingAttachmentForm
              artistaccountcode={recording.artistaccountcode}
              recordingamendmentidentifier={recording.recordingamendmentidentifier}
              recordingidentifier={recording.recordingidentifier}
              workcode={recording.workcode ?? ""}
              closeModal={() => setRecordingAttachmentModalIsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={mp3PlayerModalIsOpen} onOpenChange={setMp3PlayerModalIsOpen}>
        <DialogContent className="max-w-md">
          <div className="my-16 audioContainer">
            <div className="flex flex-col items-center gap-6 mb-4 md:flex-row">
              <div className="stockImage w-fit" />
              <div className="flex flex-col items-center gap-4 md:items-start">
                {recording.title != null && recording.title !== "" && (
                  <DialogTitle asChild>
                    <Heading as="h4">{recording.title}</Heading>
                  </DialogTitle>
                )}
                {recording.performingartist != null && recording.performingartist !== "" && (
                  <DialogDescription asChild>
                    <Text as="bodySm" className="italic text-ink-secondary">{recording.performingartist}</Text>
                  </DialogDescription>
                )}
              </div>
            </div>

            {recordingMp3Url !== "" && <AudioPlayer autoPlay src={recordingMp3Url} />}
          </div>
        </DialogContent>
      </Dialog>

      {isDeleting || isDeleted
        ? (
            <div className="flex items-center justify-center w-full h-20">
              <LoadingSmall />
            </div>
          )
        : (
            <div
              className={`flex flex-wrap items-center py-2  ${
                last ? "" : "border-b border-stroke-default"
              } `}
            >
              {(recordingInfo?.spotify_id != null || recordingInfo?.mp3 != null) && (
                <div className="w-full h-20 mb-3 ">
                  {recordingInfo?.spotify_id != null && (
                    <iframe
                      sandbox="allow-scripts allow-same-origin"
                      title="preview"
                      src={`https://open.spotify.com/embed/track/${recordingInfo.spotify_id}`}
                      className="w-full rounded-xl"
                      height="80"
                      allow="encrypted-media"
                      style={{ borderRadius: "15px", overflow: "hidden" }}
                    />
                  )}
                </div>
              )}
              <div style={{ flexGrow: 1 }} className="flex flex-col gap-2">
                <Heading as="h4">
                  {" "}
                  {recording.title}
                  {" - "}
                  {recording.performingartist}
                </Heading>
                <div className="flex flex-col gap-2 text-xs text-ink-secondary sm:gap-0">
                  <p>
                    ISRC:
                    {recording.isrc != null && recording.isrc !== "" ? recording.isrc : "-"}
                  </p>
                  <p>
                    {t("songs:release-date")}
                    :
                    {" "}
                    {recording.releasedate != null && recording.releasedate !== "0000-00-00"
                      ? recording.releasedate.split("T")[0]
                      : " - "}
                  </p>

                  <p>
                    {t("songs:record-label-caps")}
                    :
                    {recording.recordlabel != null && recording.recordlabel !== "" ? recording.recordlabel : "-"}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-start gap-2 mt-4 md:flex-row xl:mt-0 lg:justify-end xl:items-end ">
                <Button
                  variant="secondary"
                  label={String(attachMp3lbl)}
                  onClick={() => setRecordingAttachmentModalIsOpen(true)}
                  disabled={!adminRights || isDeleting}
                />

                {hasMp3 && (
                  <Button
                    variant="secondary"
                    label={String(t("songs:play-mp3"))}
                    onClick={() => {
                      setRecordingMp3Url(recording.s3presignedurl ?? "");
                      setMp3PlayerModalIsOpen(true);
                    }}
                    disabled={!adminRights || isDeleting}
                  />
                )}
                <Button
                  variant="destructive"
                  label={String(t("common:delete", "Delete"))}
                  icon={<IconBin size="md" className="stroke-current" />}
                  onClick={handleDeleteRecordingClick}
                  disabled={!adminRights || isDeleting}
                />
              </div>
            </div>
          )}
    </>
  );
}

export default SongRecording;
